<template>
  <div id="app">
    <transition name="fade">
      <Loading v-if="isLoading"></Loading>
    </transition>
    <!-- drawer -->
    <drawer-menu v-if="directory" :header-back="headerBack" :drawer-show="drawerShow" :logout="logout" @handle-click-menu="closeDrawer"></drawer-menu>
    <!-- /drawer -->
    <div class="wrapper" :class="{ 'is-open': drawerShow, 'height-wrapper': !isQrScanClosed }">
      <!-- header -->
      <header v-if="headerDisplay && !isWebView && !headerNameOnly && !(useLinkageAppAndNecFlag && nativeType)" class="header">
        <button v-if="isShowButtonBack" class="header-btn" type="button" @click="historyBack">
          <i class="icon icon-back header-btn-icon"></i>
        </button>
        <button v-if="!headerBack" class="header-btn" type="button" @click="drawer">
          <i class="icon icon-drawer header-btn-icon"></i>
        </button>
        <template v-if="isHeaderLogoShown">
          <h1 v-if="headerLogo" class="header-logo"><img class="header-logo-img" :src="headerLogo" /></h1>
          <h1 v-else class="header-headline">{{ eventName }}</h1>
        </template>
        <h1 v-else class="header-headline">{{ headerName }}</h1>
        <bell-notifications v-if="isShowBell"></bell-notifications>
        <div v-else-if="isBellNotShown" class="header-btn"></div>
      </header>
      <header v-if="headerDisplay && headerNameOnly && !isWebView && !(useLinkageAppAndNecFlag && nativeType)" class="header">
        <h1 class="header-headline">{{ headerName }}</h1>
      </header>
      <!-- /header -->
      <template v-if="isQrScanOpened && !isQrScanClosed">
        <qrcode-stream
          @detect="onDetect"
          @camera-on="onCameraOn"
          @error="onCameraError"
          :constraints="constraints"
        ></qrcode-stream>
      </template>
      <template v-else>
        <router-view v-slot="{ Component }" :key="componentKey">
          <keep-alive :include="['SearchCoupon', 'SearchShop', 'Coupon']" :exclude="excludedComponents">
            <component :is="Component" @page-data="pageData" :is-logged-in="isLoggedIn" />
          </keep-alive>
        </router-view>
      </template>
    </div>
    <div v-if="serverLocationShow" class="serverLocation">
      <p class="serverLocation-text">{{ serverLocation }}</p>
    </div>
    <ErrorModal />
    <PointModal
      v-if="pointModalShow"
      :is-show-modal="pointModalShow"
      :point="pointCode"
      :is-payment="pointStatus"
      :handle-close-modal="handleClose"
    />
    <TermsOfService v-if="isTermsModalShown" />
    <button v-if="isMenuIconShown" class="appDrawer" type="button" @click="drawer"><img :src="iconMenu" alt=""></button>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, onBeforeMount } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loading from '@/components/Loading.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import DrawerMenu from '@/components/DrawerMenu.vue';
import BellNotifications from '@/components/BellNotifications.vue';
import PointModal from '@/components/PointModal.vue';
import { QrcodeStream } from 'vue-qrcode-reader';
import TermsOfService from '@/components/TermsOfService.vue';
import { useCamera } from '@/mixins/camera';
import { usePointModal } from '@/mixins/pointModal';

const headerName = ref('');
const headerBack = ref(true);
const headerDisplay = ref(true);
const headerNameOnly = ref(false);
const drawerShow = ref(false);
const isHeaderLogoShown = ref(false);
const serverLocationShow = ref(false);
const serverLocation = ref('');
const excludedComponents = ref([]);
const componentKey = ref(0);

const store = useStore();
const route = useRoute();
const router = useRouter();

const isLoggedIn = computed(() => store.getters['auth/token']);
const isLoading = computed(() => store.getters['common/isLoading']);
const eventInfo = computed(() => store.getters['event/childEventInfo']);
const isWebView = computed(() => store.getters['common/isWebView']);
const biometricToken = computed(() => store.getters['auth/biometricToken']);
const isQrScanClosed = computed(() => store.getters['common/isQrScanClosed']);
const qrPointData = computed(() => store.getters['common/qrPointData']);
const infor = computed(() => store.getters['auth/infor']);
const useLinkageAppAndNecFlag = computed(() => store.getters['event/useLinkageAppAndNecFlag']);
const nativeType = computed(() => store.getters['event/nativeType']);
const isShowButtonBack = computed(() => {
  const isBackChecked = (route.name === 'IdentitySMS' && !infor.value?.isExpiredSms) ||
    (route.name === 'ChildIdentityConfirm' && !infor.value?.isExpiredIdentity);
  return headerBack.value && (!['MailRegistFinish', 'MailFinish', 'ParentPointFinish', 'LoginParent', 'LoginSmsParent', 'IdentitySMS', 'ChildIdentityConfirm', 'PincodeDefault', 'EntryFinish', 'IdentityFinish', 'EntryInputConfirm', 'IdentitySmsFinish'].includes(route.name) || isBackChecked);
});
const directory = computed(() => route.params.directory);
const headerLogo = computed(() => eventInfo.value?.headerLogo);
const eventName = computed(() => eventInfo.value?.name || '');
const isTermsModalShown = computed(() => !['Login', 'LoginSms', 'IdentitySMS', 'IdentityInputCodeSMS', 'IdentitySmsFinish'].includes(route.name));
const isShowBell = computed(() => {
  const specialRoute = ['ChildIdentityConfirm', 'EntryOptionalQR', 'Delete', 'DeleteFinish', 'IdentitySMS', 'IdentitySmsFinish', 'IdentityInputCodeSMS', 'IdentityFinish', 'EntryConfirm', 'EntryInputConfirm', 'EntrySmsInputConfirm', 'EntrySmsConfirm', 'EntrySmsCode', 'EntryCode', 'EntryFinish', 'ContactChildConfirm', 'ChildPointKeyword'];
  const isBellChecked = route.name === 'ChildIdentityConfirm' && !infor.value?.isExpiredIdentity;
  return directory.value && (!specialRoute.includes(route.name) || isBellChecked);
});
const email = computed(() => infor.value?.email || localStorage.getItem('clientId'));
const iconMenu = computed(() => `/assets/img/icon/appDrawer-${!drawerShow.value ? 'open' : 'close'}.svg`);
const isBellNotShown = computed(() => !isShowBell.value && (!headerBack.value || isShowButtonBack.value));
const isMenuIconShown = computed(() => {
  if(['ContactConfirm', 'ContactChildConfirm'].includes(route.name)) return false;
  return (isWebView.value || (useLinkageAppAndNecFlag.value && nativeType.value)) && directory.value;
});

const {
  openedScanQr,
  onDetect,
  onCameraOn,
  onCameraError,
  constraints,
  isQrScanOpened
} = useCamera((code) => handleScanSuccess(code));
const {
  pointModalShow,
  pointStatus,
  pointCode,
  handleClose,
  showPointModal,
  handleScanSuccess
} = usePointModal({ isQrScanOpened, constraints });

const logout = async () => {
  drawerShow.value = false;
  await store.dispatch('auth/logout');
  Promise.all([
    store.dispatch('event/getParentEventInfo'),
    store.dispatch('event/getChildEventInfo')
  ]).finally(() => {
    if (route.name !== 'Home') {
      router.push({ name: 'Home' });
    }
  });
};
const drawer = async () => {
  drawerShow.value = !drawerShow.value;
  await Promise.all([
    store.dispatch('event/getParentEventInfo'),
    store.dispatch('event/getChildEventInfo')
  ]);
};
const closeDrawer = async(data) => {
  if (data?.isButtonQR) {
    openedScanQr();
  } else {
    store.commit('common/CLOSE_MODAL_QR_SCAN', true);
  }
  drawerShow.value = false;
};

const pageData = ({ pageName, pageBack, pageLogo, display, pageNameOnly }) => {
  headerName.value = pageName?.value;
  headerBack.value = pageBack?.value !== undefined ? false : true;
  isHeaderLogoShown.value = pageLogo?.value !== undefined;
  headerDisplay.value = display?.value !== undefined ? false : true;
  headerNameOnly.value = pageNameOnly?.value !== undefined ? true : false;
};
const height = () => {
  let winH = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${winH}px`);
};
const resize = () => {
  height();
};

watch(email, (newVal, oldVal) => {
  if ((isWebView.value || useLinkageAppAndNecFlag.value) && isLoggedIn.value && newVal && (!biometricToken.value || oldVal !== newVal)) {
    store.dispatch('auth/createBiometricToken', newVal);
    localStorage.setItem('clientId', newVal);
  }
}, { deep: true });
watch(() => route.path, async (to, from) => {
  if (to !== from) {
    if (drawerShow.value) closeDrawer();
  }
  if ((to !== from) && isQrScanOpened.value) {
    isQrScanOpened.value = false;
    store.dispatch('common/openErrorModal', false);
    store.dispatch('common/setErrors', []);
    store.dispatch('common/setModalTitle', '');
  }
  if (from.name === 'ChildPointKeyword' && to.name !== 'Mypage') {
    if (qrPointData.value) showPointModal(qrPointData.value);
  }
});

watch(() => route.name, (to, from) => {
  if ((['Login', 'LoginSms', 'EventList', 'Home'].includes(to) && from === 'SearchCoupon') ||
    (to === 'EventList' && from === 'CouponFinish') ||
    (to === 'LoginParent' && from === 'SearchCoupon') ||
    (to === 'LoginParent' && from === 'CouponDetail') ||
    (to === 'SearchCoupon' && from === 'Home'))
  {
    if (!excludedComponents.value.includes('SearchCoupon')) {
      excludedComponents.value.push('SearchCoupon');
    }
    componentKey.value++;
  } else if(['LoginParent', 'Login', 'LoginSms', 'LoginSmsParent'].includes(to) && from === 'SearchShop') {
    if (!excludedComponents.value.includes('SearchShop')) {
      excludedComponents.value.push('SearchShop');
    }
    componentKey.value++;
  } else if(['LoginParent', 'SearchShop'].includes(to) && from === 'Coupon' ||
    (to === 'Shop' && (from === 'CouponDetail' || from === 'Coupon')) ||
    (to === 'Coupon' && ['Shop', 'Menu', 'Gallery'].includes(from)) ||
    (to === 'CouponDetail' && from === 'Coupon'))
  {
    if (!excludedComponents.value.includes('Coupon')) {
      excludedComponents.value.push('Coupon');
    }
    componentKey.value++;
  }
});

onMounted(async () => {
  window.addEventListener('resize', resize());
});

onBeforeMount(() => {
  height();
  if (location.origin.match(/localhost/)) {
    serverLocationShow.value = true;
    serverLocation.value = 'localhost';
  }
  if (navigator.userAgent.includes('PalletApp')) {
    store.commit('common/SET_IS_WEB_VIEW', true);
  } else if (navigator.userAgent.includes('LinkageApp')) {
    store.commit('common/SET_IS_LINKAGE_VIEW', true);
  }
  if (isWebView.value || useLinkageAppAndNecFlag.value) {
    window.omise = {};
    window.omise.qrcodeScanCompleted = (qr_code) => {
      handleScanSuccess(qr_code);
    };
    window.omise.qrcodeScanFailed = function (error) {
      alert(error);
    };
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', resize);
  store.commit('common/RESET_QR_POINT_DATA', null);
});
</script>
